import React, { useState, useEffect, useRef } from 'react';
import { customAppController } from '../../controllers/customAppController';
import {
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle,
  ChevronUp, ChevronDown, Smartphone, Package, Clock, CheckCircle2,
  Settings, Palette, Image as ImageIcon, Code, Upload, RefreshCw,
  History, ArrowUpCircle, FileArchive, ChevronRight, Lock,
  Globe, EyeOff, ArrowRightLeft, Send,
} from 'lucide-react';

/* ─────────────────────────────────────────────
   Style tokens
───────────────────────────────────────────── */
const S = {
  modal: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 20,
  },
  card: {
    background: '#fff', borderRadius: 14, width: '100%',
    maxWidth: 1000, maxHeight: '92vh', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  hdr: {
    padding: '18px 24px', borderBottom: '1px solid #e2e8f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
  },
  title: { fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 },
  ftr: {
    padding: '14px 24px', borderTop: '1px solid #e2e8f0',
    display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0, alignItems: 'center',
  },
  btnPrimary:   { display:'inline-flex',alignItems:'center',gap:6,padding:'9px 18px',background:'#3b82f6',color:'#fff',border:'1px solid #3b82f6',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' },
  btnSecondary: { display:'inline-flex',alignItems:'center',gap:6,padding:'9px 18px',background:'#fff',color:'#374151',border:'1px solid #d1d5db',borderRadius:8,fontSize:13,fontWeight:500,cursor:'pointer' },
  btnDanger:    { display:'inline-flex',alignItems:'center',gap:6,padding:'9px 18px',background:'#fef2f2',color:'#dc2626',border:'1px solid #fecaca',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' },
  btnSuccess:   { display:'inline-flex',alignItems:'center',gap:6,padding:'9px 18px',background:'#10b981',color:'#fff',border:'1px solid #10b981',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' },
  btnWarning:   { display:'inline-flex',alignItems:'center',gap:6,padding:'9px 18px',background:'#f59e0b',color:'#fff',border:'1px solid #f59e0b',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer' },
  iconBtn:      { padding:6,background:'transparent',border:'none',borderRadius:6,cursor:'pointer',color:'#64748b',display:'flex',alignItems:'center' },
  smallBtn:     { padding:'5px 8px',background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:6,cursor:'pointer',display:'flex',alignItems:'center',gap:4,fontSize:12,color:'#374151' },
  label:        { display:'block',fontSize:13,fontWeight:600,color:'#374151',marginBottom:6 },
  input:        { width:'100%',padding:'9px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,outline:'none',color:'#1e293b',background:'#fff',boxSizing:'border-box' },
  table:        { width:'100%',borderCollapse:'collapse' },
  th:           { background:'#f8fafc',padding:'12px 14px',textAlign:'left',fontSize:12,fontWeight:700,color:'#475569',borderBottom:'1px solid #e2e8f0',whiteSpace:'nowrap' },
  td:           { padding:'12px 14px',borderBottom:'1px solid #f1f5f9',verticalAlign:'middle' },
  center:       { padding:40,textAlign:'center',color:'#94a3b8',fontSize:14 },
};

/* ─────────────────────────────────────────────
   Track pipeline  internal→alpha→beta→production
───────────────────────────────────────────── */
const TRACKS = [
  { id:'internal',   label:'Internal Review', shortLabel:'Internal',   desc:'Up to 100 internal testers', color:'#6366f1', bg:'#eef2ff', textColor:'#3730a3', step:1 },
  { id:'alpha',      label:'Alpha Testing',   shortLabel:'Alpha',      desc:'Closed testing group',        color:'#f59e0b', bg:'#fef3c7', textColor:'#92400e', step:2 },
  { id:'beta',       label:'Beta Testing',    shortLabel:'Beta',       desc:'Open testing program',         color:'#3b82f6', bg:'#dbeafe', textColor:'#1e40af', step:3 },
  { id:'production', label:'Production',      shortLabel:'Production', desc:'All users on Play Store',     color:'#10b981', bg:'#dcfce7', textColor:'#166534', step:4 },
];
const getTrack    = id  => TRACKS.find(t => t.id === id) || TRACKS[0];
const defaultNext = cur => { if (!cur) return 'internal'; const t = getTrack(cur); const n = TRACKS.find(x => x.step === t.step + 1); return n ? n.id : 'production'; };

/* ─────────────────────────────────────────────
   Atoms
───────────────────────────────────────────── */
const Badge = ({ color, bg, children, style={} }) => (
  <span style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'3px 10px',borderRadius:20,fontSize:12,fontWeight:600,color,backgroundColor:bg,...style }}>{children}</span>
);
const StatusBadge = ({ status }) => {
  const map = { Published:{color:'#166534',bg:'#dcfce7'}, Building:{color:'#92400e',bg:'#fef3c7'}, Draft:{color:'#374151',bg:'#f3f4f6'}, Failed:{color:'#991b1b',bg:'#fee2e2'} };
  const s = map[status] || { color:'#374151',bg:'#f3f4f6' };
  return <Badge color={s.color} bg={s.bg}>{status}</Badge>;
};
const TrackBadge      = ({ track }) => { const t = getTrack(track); return <Badge color={t.textColor} bg={t.bg}>{t.label}</Badge>; };
const VisibilityBadge = ({ isPublic }) => isPublic
  ? <Badge color="#166534" bg="#dcfce7"><Globe size={11} style={{marginRight:2}}/>Public</Badge>
  : <Badge color="#374151" bg="#f3f4f6"><EyeOff size={11} style={{marginRight:2}}/>Private</Badge>;
const PublishBadge    = ({ published }) => published
  ? <Badge color="#166534" bg="#dcfce7"><Send size={11} style={{marginRight:2}}/>Published</Badge>
  : <Badge color="#64748b" bg="#f3f4f6">Unpublished</Badge>;

const ProgressBar = ({ pct, color='#3b82f6' }) => (
  <div style={{ height:6,background:'#e5e7eb',borderRadius:3,overflow:'hidden',minWidth:80 }}>
    <div style={{ height:'100%',width:`${pct}%`,background:color,borderRadius:3,transition:'width .4s' }} />
  </div>
);
const SectionTitle = ({ children }) => (
  <div style={{ fontSize:15,fontWeight:700,color:'#1e293b',marginBottom:12,display:'flex',alignItems:'center' }}>{children}</div>
);
const FormField = ({ label, required, hint, children }) => (
  <div>
    <label style={S.label}>{label}{required && <span style={{color:'#ef4444',marginLeft:2}}>*</span>}</label>
    {children}
    {hint && <div style={{fontSize:11,color:'#94a3b8',marginTop:4}}>{hint}</div>}
  </div>
);
const ErrorBox = ({ errors }) => errors.length === 0 ? null : (
  <div style={{ background:'#fee2e2',border:'1px solid #fecaca',borderRadius:8,padding:'12px 16px',marginBottom:16 }}>
    {errors.map((e,i) => (
      <div key={i} style={{ display:'flex',alignItems:'center',gap:8,color:'#dc2626',fontSize:13,marginBottom:i<errors.length-1?4:0 }}>
        <AlertCircle size={14} style={{flexShrink:0}}/>{e}
      </div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────
   TrackPipeline selector
───────────────────────────────────────────── */
const TrackPipeline = ({ value, onChange, currentAPKTrack, allowAll=false }) => {
  const currentStep = currentAPKTrack ? getTrack(currentAPKTrack).step : 0;
  return (
    <div>
      <label style={S.label}>Release Track Pipeline *</label>
      <div style={{ display:'flex',alignItems:'center',flexWrap:'wrap',rowGap:6,background:'#f8fafc',borderRadius:10,padding:'10px 14px',marginBottom:12,border:'1px solid #e2e8f0' }}>
        {TRACKS.map((t,idx) => {
          const done = t.step < currentStep; const current = t.step === currentStep;
          return (
            <React.Fragment key={t.id}>
              <div style={{ display:'flex',alignItems:'center',gap:5,padding:'3px 10px',borderRadius:20,background:done?'#f0fdf4':current?t.bg:'transparent',border:`1px solid ${done?'#bbf7d0':current?t.color:'#e2e8f0'}`,fontSize:12,fontWeight:current?700:400,color:done?'#166534':current?t.textColor:'#94a3b8' }}>
                {done && <span>✓</span>}{t.shortLabel}
              </div>
              {idx < TRACKS.length-1 && <ChevronRight size={13} color="#cbd5e1" style={{flexShrink:0}}/>}
            </React.Fragment>
          );
        })}
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10 }}>
        {TRACKS.map(t => {
          const locked = !allowAll && t.step < currentStep;
          const selected = value === t.id;
          const isNext = t.step === currentStep + 1;
          return (
            <div key={t.id} onClick={() => !locked && onChange(t.id)}
              style={{ padding:'12px 14px',borderRadius:10,position:'relative',cursor:locked?'not-allowed':'pointer',transition:'all .15s',opacity:locked?0.42:1,border:`2px solid ${selected?t.color:isNext?t.color+'55':'#e2e8f0'}`,background:selected?t.bg:isNext?t.bg+'44':'#fff' }}>
              {isNext && !selected && (
                <div style={{ position:'absolute',top:6,right:8,fontSize:10,fontWeight:700,color:t.textColor,background:t.bg,padding:'2px 6px',borderRadius:8,border:`1px solid ${t.color}` }}>Next</div>
              )}
              {locked && <div style={{position:'absolute',top:8,right:10}}><Lock size={12} color="#94a3b8"/></div>}
              <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:4 }}>
                <div style={{ width:26,height:26,borderRadius:'50%',background:selected?t.color:'#f1f5f9',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,color:selected?'#fff':'#64748b' }}>{t.step}</div>
                <div style={{ fontWeight:700,fontSize:13,color:selected?t.textColor:'#1e293b' }}>{t.label}</div>
              </div>
              <div style={{fontSize:12,color:'#64748b',paddingLeft:34}}>{t.desc}</div>
            </div>
          );
        })}
      </div>
      {value && (
        <div style={{ marginTop:10,padding:'8px 12px',borderRadius:8,background:getTrack(value).bg,border:`1px solid ${getTrack(value).color}40`,fontSize:12,color:getTrack(value).textColor,fontWeight:600,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap' }}>
          <span>Selected:</span><TrackBadge track={value}/>
          {value==='production' && <span style={{fontWeight:400,color:'#374151'}}>— Goes live to all users after publish</span>}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   VisibilityToggle
───────────────────────────────────────────── */
const VisibilityToggle = ({ value, onChange }) => (
  <div>
    <label style={S.label}>App Visibility *</label>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
      {[
        { val:true,  icon:<Globe size={18}/>,  label:'Public App',  desc:'Visible to all users on Play Store', color:'#10b981',bg:'#dcfce7',textColor:'#166534' },
        { val:false, icon:<EyeOff size={18}/>, label:'Private App', desc:'Only invited users can access',      color:'#6366f1',bg:'#eef2ff',textColor:'#3730a3' },
      ].map(opt => (
        <div key={String(opt.val)} onClick={() => onChange(opt.val)}
          style={{ padding:'12px 14px',borderRadius:10,cursor:'pointer',transition:'all .15s',border:`2px solid ${value===opt.val?opt.color:'#e2e8f0'}`,background:value===opt.val?opt.bg:'#fff' }}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
            <div style={{ width:34,height:34,borderRadius:8,background:value===opt.val?opt.color+'20':'#f1f5f9',display:'flex',alignItems:'center',justifyContent:'center',color:value===opt.val?opt.color:'#94a3b8' }}>{opt.icon}</div>
            <div style={{fontWeight:700,fontSize:13,color:value===opt.val?opt.textColor:'#1e293b'}}>{opt.label}</div>
          </div>
          <div style={{fontSize:12,color:'#64748b',paddingLeft:42}}>{opt.desc}</div>
        </div>
      ))}
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   PublishAPKModal — publish confirmation
───────────────────────────────────────────── */
const PublishAPKModal = ({ app, apk, onClose, onSuccess }) => {
  const [publishing, setPublishing] = useState(false);
  const [errors,     setErrors]     = useState([]);

  const handlePublish = async () => {
    setPublishing(true); setErrors([]);
    const res = await customAppController.publishAPK(app.id || app._id, apk.id);
    setPublishing(false);
    if (res.success) {
      onSuccess(res.message || `APK v${apk.versionName} published successfully!`);
      onClose();
    } else {
      setErrors([res.message || 'Failed to publish APK']);
    }
  };

  return (
    <div style={{ ...S.modal, zIndex: 1200 }} onClick={onClose}>
      <div style={{ ...S.card, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div style={S.hdr}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <Send size={18} color="#10b981"/>
            <h2 style={S.title}>Publish APK Release</h2>
          </div>
          <button style={S.iconBtn} onClick={onClose}><X size={20}/></button>
        </div>
        <div style={{padding:'20px 24px', flex:1, overflowY:'auto'}}>
          {/* APK info */}
          <div style={{ background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:8,padding:'14px 16px',marginBottom:18,display:'flex',gap:12,alignItems:'center' }}>
            <div style={{fontSize:32}}>🚀</div>
            <div>
              <div style={{fontWeight:700,fontSize:14,color:'#166534'}}>v{apk.versionName} — {apk.fileName}</div>
              <div style={{fontSize:12,color:'#15803d',marginTop:4,display:'flex',gap:6,alignItems:'center'}}>
                <TrackBadge track={apk.track}/>
                <VisibilityBadge isPublic={apk.isPublic !== false}/>
              </div>
            </div>
          </div>

          <ErrorBox errors={errors}/>

          {/* Checklist */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,color:'#1e293b',marginBottom:10}}>Before publishing, confirm:</div>
            {[
              ['APK is tested on all target devices', apk.track !== 'internal'],
              ['Version code is unique', true],
              ['SHA key is verified', !!apk.shaKey],
              ['Release notes are added', !!apk.releaseNotes],
              ['Track is Production', apk.track === 'production'],
            ].map(([label, ok]) => (
              <div key={label} style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,fontSize:13}}>
                <div style={{ width:18,height:18,borderRadius:'50%',background:ok?'#dcfce7':'#fee2e2',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                  <span style={{fontSize:10,fontWeight:700,color:ok?'#166534':'#dc2626'}}>{ok?'✓':'!'}</span>
                </div>
                <span style={{color:ok?'#374151':'#dc2626'}}>{label}</span>
              </div>
            ))}
          </div>

          {apk.track !== 'production' && (
            <div style={{ background:'#fef3c7',border:'1px solid #f59e0b40',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#92400e',display:'flex',gap:8 }}>
              <AlertCircle size={16} style={{flexShrink:0,marginTop:1}}/>
              <span>This APK is on <strong>{getTrack(apk.track).label}</strong> track. For best practice, move it to <strong>Production</strong> track before publishing.</span>
            </div>
          )}
        </div>
        <div style={S.ftr}>
          <button style={S.btnSecondary} onClick={onClose} disabled={publishing}>Cancel</button>
          <button style={S.btnSuccess} onClick={handlePublish} disabled={publishing}>
            {publishing ? 'Publishing...' : <><Send size={15}/> Publish Release</>}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   ChangeTrackModal
───────────────────────────────────────────── */
const ChangeTrackModal = ({ app, apk, onClose, onSuccess }) => {
  const [track,  setTrack]  = useState(apk.track);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleSave = async () => {
    if (track === apk.track) { onClose(); return; }
    setSaving(true); setErrors([]);
    const res = await customAppController.updateAPKTrack(app.id || app._id, apk.id, track);
    setSaving(false);
    if (res.success) { onSuccess(res.message || `Track updated to ${getTrack(track).label}`); onClose(); }
    else setErrors([res.message || 'Failed to update track']);
  };

  const sel = getTrack(track);
  return (
    <div style={{...S.modal,zIndex:1100}} onClick={onClose}>
      <div style={{...S.card,maxWidth:580}} onClick={e=>e.stopPropagation()}>
        <div style={S.hdr}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <ArrowRightLeft size={18} color="#3b82f6"/>
            <h2 style={S.title}>Change Release Track</h2>
          </div>
          <button style={S.iconBtn} onClick={onClose}><X size={20}/></button>
        </div>
        <div style={{padding:24,overflowY:'auto',flex:1}}>
          <div style={{ background:'#f8fafc',borderRadius:8,padding:'12px 16px',marginBottom:20,border:'1px solid #e2e8f0',display:'flex',alignItems:'center',gap:12 }}>
            <FileArchive size={20} color="#64748b"/>
            <div>
              <div style={{fontWeight:600,fontSize:13,color:'#1e293b'}}>v{apk.versionName} (Code: {apk.versionCode})</div>
              <div style={{fontSize:12,color:'#64748b',marginTop:3,display:'flex',alignItems:'center',gap:6}}>
                Current: <TrackBadge track={apk.track}/>
                {apk.isActive && <Badge color="#166534" bg="#dcfce7">Active</Badge>}
                {apk.isPublished && <Badge color="#166534" bg="#dcfce7"><Send size={10} style={{marginRight:2}}/>Published</Badge>}
              </div>
            </div>
          </div>
          <ErrorBox errors={errors}/>
          <TrackPipeline value={track} onChange={setTrack} currentAPKTrack={null} allowAll={true}/>
          {track === 'production' && track !== apk.track && (
            <div style={{ marginTop:14,padding:'10px 14px',background:'#fef3c7',border:'1px solid #f59e0b50',borderRadius:8,fontSize:13,color:'#92400e',display:'flex',gap:8 }}>
              <AlertCircle size={16} style={{flexShrink:0,marginTop:1}}/>
              <span>Moving to <strong>Production</strong> will deactivate any existing production APK and make this one the active release.</span>
            </div>
          )}
        </div>
        <div style={S.ftr}>
          <div style={{fontSize:12,color:'#64748b',flex:1}}>
            {track !== apk.track ? `${getTrack(apk.track).label} → ${sel.label}` : 'No change'}
          </div>
          <button style={S.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button
            style={{...S.btnPrimary,background:track!==apk.track?sel.color:'#94a3b8',borderColor:track!==apk.track?sel.color:'#94a3b8',cursor:track!==apk.track?'pointer':'not-allowed'}}
            onClick={handleSave} disabled={saving||track===apk.track}>
            {saving ? 'Moving...' : `Move to ${sel.label}`}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   APK History Modal — with Publish button,
   grouped by track, active/inactive guard
───────────────────────────────────────────── */
const APKHistoryModal = ({ app, onClose, onDownload, onChangeTrack }) => {
  const [history,         setHistory]         = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [changeTrackAPK,  setChangeTrackAPK]  = useState(null);
  const [publishAPK,      setPublishAPK]      = useState(null);
  const [activating,      setActivating]      = useState(null);
  const [confirmDeact,    setConfirmDeact]     = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await customAppController.getAPKHistory(app.id || app._id);
    if (res.success) setHistory(res.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [app]);

  const handleActivate = async (apkId) => {
    const apk = history.find(h => h.id === apkId);
    if (!apk) return;
    if (apk.isActive) { alert(`⚠️ APK v${apk.versionName} is already active on the ${getTrack(apk.track).label} track.`); return; }
    const currentActive = history.find(h => h.track === apk.track && h.isActive && h.id !== apkId);
    if (currentActive) { setConfirmDeact({ apk, currentActive }); return; }
    await doActivate(apkId);
  };

  const doActivate = async (apkId) => {
    setActivating(apkId);
    const res = await customAppController.setActiveAPK(app.id || app._id, apkId);
    setActivating(null); setConfirmDeact(null);
    if (res.success) { await load(); alert('✅ ' + res.message); }
    else if (res.errorCode === 'ALREADY_ACTIVE') alert(`⚠️ ${res.message}`);
    else alert('❌ ' + (res.message || 'Failed to activate'));
  };

  const trackOrder = ['production','beta','alpha','internal'];
  const grouped = trackOrder.reduce((acc, t) => {
    const items = history.filter(h => h.track === t).sort((a,b) => new Date(b.uploadedAt)-new Date(a.uploadedAt));
    if (items.length) acc[t] = items;
    return acc;
  }, {});

  return (
    <>
      <div style={S.modal} onClick={onClose}>
        <div style={{...S.card,maxWidth:1120}} onClick={e=>e.stopPropagation()}>
          <div style={S.hdr}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <History size={20} color="#3b82f6"/>
              <h2 style={S.title}>APK History — {app.appName}</h2>
            </div>
            <button style={S.iconBtn} onClick={onClose}><X size={20}/></button>
          </div>

          <div style={{padding:'20px 24px',overflowY:'auto',maxHeight:'calc(90vh - 140px)'}}>
            {loading ? <div style={S.center}>Loading history...</div>
            : history.length === 0 ? <div style={S.center}>No APK uploads yet.</div>
            : Object.entries(grouped).map(([track, apks]) => {
                const t = getTrack(track);
                const activeAPK = apks.find(a => a.isActive);
                return (
                  <div key={track} style={{marginBottom:28}}>
                    {/* Track header */}
                    <div style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 16px',background:t.bg,borderRadius:'10px 10px 0 0',border:`1px solid ${t.color}30`,borderBottom:'none' }}>
                      <TrackBadge track={track}/>
                      <span style={{fontSize:13,color:t.textColor,fontWeight:600}}>{apks.length} version{apks.length>1?'s':''}</span>
                      {activeAPK && (
                        <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:8}}>
                          <Badge color="#166534" bg="#dcfce7">✓ Active: v{activeAPK.versionName}</Badge>
                          {activeAPK.isPublished && <Badge color="#166534" bg="#dcfce7"><Send size={10} style={{marginRight:2}}/>Published</Badge>}
                        </div>
                      )}
                    </div>

                    <table style={{...S.table,border:`1px solid ${t.color}30`,borderTop:'none',borderRadius:'0 0 10px 10px',overflow:'hidden'}}>
                      <thead>
                        <tr>
                          {['Version','Visibility','Published','SHA Key','Checksum','File','Size','Rollout','Uploaded','Status','Actions'].map(h => (
                            <th key={h} style={{...S.th,background:t.bg+'80'}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {apks.map((apk, i) => (
                          <tr key={apk.id||i} style={{background:apk.isActive?'#f0fdf4':i%2===0?'#fff':'#fafafa'}}>
                            <td style={S.td}>
                              <div style={{fontWeight:700,fontSize:13,color:apk.isActive?'#166534':'#1e293b'}}>
                                v{apk.versionName}{apk.isActive&&<span style={{marginLeft:5,fontSize:10,color:'#10b981'}}>★</span>}
                              </div>
                              <div style={{fontSize:11,color:'#64748b',fontFamily:'monospace'}}>Code: {apk.versionCode}</div>
                            </td>
                            <td style={S.td}><VisibilityBadge isPublic={apk.isPublic!==false}/></td>
                            <td style={S.td}><PublishBadge published={!!apk.isPublished}/></td>
                            <td style={S.td}>
                              <div style={{fontSize:11,fontFamily:'monospace',color:'#475569',background:'#f1f5f9',padding:'2px 6px',borderRadius:4,maxWidth:110,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={apk.shaKey||'—'}>
                                {apk.shaKey?apk.shaKey.slice(0,18)+'…':'—'}
                              </div>
                            </td>
                            <td style={S.td}>
                              <div style={{fontSize:10,fontFamily:'monospace',color:'#94a3b8',maxWidth:100,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={apk.checksum||''}>
                                {apk.checksum?apk.checksum.slice(0,12)+'…':'—'}
                              </div>
                            </td>
                            <td style={S.td}><div style={{fontSize:12,color:'#475569',maxWidth:130,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{apk.fileName}</div></td>
                            <td style={S.td}><span style={{fontSize:12,color:'#64748b'}}>{apk.fileSize}</span></td>
                            <td style={S.td}>
                              <div style={{display:'flex',flexDirection:'column',gap:3}}>
                                <span style={{fontSize:12,fontWeight:600}}>{apk.rolloutPercentage}%</span>
                                <ProgressBar pct={apk.rolloutPercentage} color={t.color}/>
                              </div>
                            </td>
                            <td style={S.td}>
                              <div style={{fontSize:12,color:'#64748b',whiteSpace:'nowrap'}}>
                                {apk.uploadedAt?new Date(apk.uploadedAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'—'}
                              </div>
                            </td>
                            <td style={S.td}>
                              {apk.isActive
                                ? <Badge color="#166534" bg="#dcfce7">✓ Active</Badge>
                                : <Badge color="#6b7280" bg="#f3f4f6">Inactive</Badge>}
                            </td>
                            <td style={S.td}>
                              <div style={{display:'flex',flexDirection:'column',gap:5,minWidth:108}}>
                                {/* Download */}
                                <button style={{...S.smallBtn,color:'#3b82f6'}} onClick={()=>onDownload(app.id||app._id,apk.id)}>
                                  <Download size={13}/> Download
                                </button>
                                {/* Change Track */}
                                <button style={{...S.smallBtn,color:'#6366f1'}} onClick={()=>setChangeTrackAPK(apk)}>
                                  <ArrowRightLeft size={13}/> Change Track
                                </button>
                                {/* Publish */}
                                {!apk.isPublished ? (
                                  <button style={{...S.smallBtn,color:'#10b981',fontWeight:600}} onClick={()=>setPublishAPK(apk)}>
                                    <Send size={13}/> Publish
                                  </button>
                                ) : (
                                  <button style={{...S.smallBtn,color:'#94a3b8',cursor:'default'}} disabled>
                                    <Send size={13}/> Published
                                  </button>
                                )}
                                {/* Activate / Already Active */}
                                {apk.isActive ? (
                                  <button style={{...S.smallBtn,color:'#94a3b8',cursor:'default'}} disabled>
                                    ✓ Already Active
                                  </button>
                                ) : (
                                  <button style={{...S.smallBtn,color:'#10b981'}} disabled={activating===apk.id} onClick={()=>handleActivate(apk.id)}>
                                    {activating===apk.id?'…':'▶ Activate'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })
            }
          </div>

          <div style={S.ftr}>
            <button style={S.btnSecondary} onClick={onClose}>Close</button>
          </div>
        </div>
      </div>

      {/* Confirm deactivate old → activate new */}
      {confirmDeact && (
        <div style={{...S.modal,zIndex:1200}} onClick={()=>setConfirmDeact(null)}>
          <div style={{...S.card,maxWidth:460}} onClick={e=>e.stopPropagation()}>
            <div style={S.hdr}>
              <h2 style={S.title}>Confirm Activation</h2>
              <button style={S.iconBtn} onClick={()=>setConfirmDeact(null)}><X size={20}/></button>
            </div>
            <div style={{padding:'20px 24px'}}>
              <div style={{ background:'#fef3c7',border:'1px solid #f59e0b40',borderRadius:8,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#92400e',display:'flex',gap:8 }}>
                <AlertCircle size={16} style={{flexShrink:0,marginTop:1}}/>
                <div>
                  <strong>v{confirmDeact.currentActive.versionName}</strong> is currently active on <TrackBadge track={confirmDeact.currentActive.track}/>.
                  <br/><br/>
                  Activating <strong>v{confirmDeact.apk.versionName}</strong> will automatically deactivate <strong>v{confirmDeact.currentActive.versionName}</strong>.
                </div>
              </div>
              {[
                ['Will deactivate', `v${confirmDeact.currentActive.versionName}`, '#dc2626'],
                ['Will activate',   `v${confirmDeact.apk.versionName}`,           '#10b981'],
                ['Track',           getTrack(confirmDeact.apk.track).label,        '#374151'],
              ].map(([k,v,c])=>(
                <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'7px 0',borderBottom:'1px solid #f1f5f9',fontSize:13}}>
                  <span style={{color:'#64748b'}}>{k}</span><span style={{fontWeight:600,color:c}}>{v}</span>
                </div>
              ))}
            </div>
            <div style={S.ftr}>
              <button style={S.btnSecondary} onClick={()=>setConfirmDeact(null)}>Cancel</button>
              <button style={S.btnSuccess}   onClick={()=>doActivate(confirmDeact.apk.id)}>
                ✓ Deactivate old & Activate new
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Track modal */}
      {changeTrackAPK && (
        <ChangeTrackModal app={app} apk={changeTrackAPK}
          onClose={()=>setChangeTrackAPK(null)}
          onSuccess={msg=>{ setChangeTrackAPK(null); load(); onChangeTrack&&onChangeTrack(msg); }}
        />
      )}

      {/* Publish APK modal */}
      {publishAPK && (
        <PublishAPKModal app={app} apk={publishAPK}
          onClose={()=>setPublishAPK(null)}
          onSuccess={msg=>{ setPublishAPK(null); load(); alert('✅ '+msg); }}
        />
      )}
    </>
  );
};

/* ─────────────────────────────────────────────
   APK Upload Modal  (3 steps)
───────────────────────────────────────────── */
const APKUploadModal = ({ app, onClose, onSuccess }) => {
  const [step, setStep]       = useState(1);
  const [apkFile, setApkFile] = useState(null);
  const [dragging, setDrag]   = useState(false);
  const [uploading, setUpl]   = useState(false);
  const [progress, setProg]   = useState(0);
  const [done, setDone]       = useState(false);
  const [errors, setErrors]   = useState([]);
  const fileRef               = useRef();

  const currentAPKTrack = app?.latestAPK?.track || null;

  const [apkData, setApkData] = useState({
    versionName: app?.version||'', versionCode:'', shaKey:'',
    track: defaultNext(currentAPKTrack), isPublic:true,
    rolloutPercentage:100, releaseNotes:'',
    minSdkVersion: app?.minSdkVersion||'21',
    targetSdkVersion: app?.targetSdkVersion||'33',
  });

  const validateFile = f => {
    setErrors([]);
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['apk','aab'].includes(ext)) { setErrors(['Only .apk or .aab files allowed.']); return; }
    if (f.size > 500*1024*1024) { setErrors(['File size must be under 500 MB.']); return; }
    setApkFile(f); setStep(2);
  };
  const handleDrop   = e => { e.preventDefault(); setDrag(false); const f=e.dataTransfer.files[0]; if(f) validateFile(f); };
  const handleChange = e => setApkData(p => ({...p,[e.target.name]:e.target.value}));

  const goReview = () => {
    const errs = [];
    if (!apkData.versionName.trim())            errs.push('Version name is required.');
    if (!apkData.versionCode.toString().trim()) errs.push('Version code is required.');
    if (!apkData.shaKey.trim())                 errs.push('SHA key is required.');
    if (errs.length) { setErrors(errs); return; }
    setErrors([]); setStep(3);
  };

  const handlePublish = async () => {
    setErrors([]); setUpl(true); setProg(0);
    const iv = setInterval(()=>setProg(p=>{if(p>=90){clearInterval(iv);return 90;}return p+8;}),200);
    const res = await customAppController.uploadAPK(app.id||app._id, apkFile, apkData);
    clearInterval(iv); setProg(100); setUpl(false);
    if (res.success) { setDone(true); setTimeout(()=>{ onSuccess(res.message||'APK uploaded successfully!'); onClose(); },800); }
    else { setProg(0); setErrors(res.errors||[res.message||'Upload failed.']); }
  };

  const sel = getTrack(apkData.track);
  const showRollout = ['production','beta'].includes(apkData.track);
  const stepLabels  = ['Select File','Release Details','Review & Publish'];

  return (
    <div style={S.modal} onClick={!uploading?onClose:undefined}>
      <div style={{...S.card,maxWidth:720}} onClick={e=>e.stopPropagation()}>
        <div style={S.hdr}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <ArrowUpCircle size={20} color="#3b82f6"/>
            <h2 style={S.title}>Upload New Release — {app?.appName}</h2>
          </div>
          {!uploading && <button style={S.iconBtn} onClick={onClose}><X size={20}/></button>}
        </div>

        {/* Stepper */}
        <div style={{padding:'12px 24px',borderBottom:'1px solid #e2e8f0',display:'flex',alignItems:'center'}}>
          {stepLabels.map((label,idx)=>{
            const num=idx+1; const active=step===num; const passed=step>num;
            return (
              <React.Fragment key={label}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{ width:26,height:26,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,background:passed?'#10b981':active?'#3b82f6':'#e5e7eb',color:(passed||active)?'#fff':'#9ca3af' }}>{passed?'✓':num}</div>
                  <span style={{fontSize:13,fontWeight:active?600:400,color:active?'#1e293b':'#64748b'}}>{label}</span>
                </div>
                {idx<2 && <ChevronRight size={16} color="#cbd5e1" style={{margin:'0 10px',flexShrink:0}}/>}
              </React.Fragment>
            );
          })}
        </div>

        <div style={{padding:24,overflowY:'auto',flex:1}}>
          <ErrorBox errors={errors}/>

          {/* Step 1 */}
          {step===1 && (
            <div style={{border:`2px dashed ${dragging?'#3b82f6':'#cbd5e1'}`,borderRadius:12,padding:'56px 20px',textAlign:'center',background:dragging?'#eff6ff':'#f8fafc',cursor:'pointer',transition:'all .2s'}}
              onDragOver={e=>{e.preventDefault();setDrag(true);}} onDragLeave={()=>setDrag(false)} onDrop={handleDrop} onClick={()=>fileRef.current.click()}>
              <div style={{fontSize:52,marginBottom:14}}>📦</div>
              <div style={{fontSize:18,fontWeight:700,color:'#1e293b',marginBottom:8}}>Drag & Drop APK / AAB</div>
              <div style={{fontSize:13,color:'#64748b',marginBottom:20}}>Supports .apk and .aab — max 500 MB</div>
              <button style={S.btnPrimary} onClick={e=>{e.stopPropagation();fileRef.current.click();}}>
                <Upload size={15}/> Browse File
              </button>
              <input ref={fileRef} type="file" accept=".apk,.aab" style={{display:'none'}} onChange={e=>e.target.files[0]&&validateFile(e.target.files[0])}/>
            </div>
          )}

          {/* Step 2 */}
          {step===2 && (
            <div style={{display:'flex',flexDirection:'column',gap:20}}>
              <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:8,padding:'12px 16px',display:'flex',alignItems:'center',gap:12}}>
                <FileArchive size={24} color="#10b981"/>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:13,color:'#166534'}}>{apkFile?.name}</div>
                  <div style={{fontSize:12,color:'#15803d'}}>{(apkFile?.size/1024/1024).toFixed(1)} MB</div>
                </div>
                <button style={S.iconBtn} onClick={()=>{setApkFile(null);setStep(1);}}><X size={16}/></button>
              </div>

              <TrackPipeline value={apkData.track} onChange={v=>setApkData(p=>({...p,track:v}))} currentAPKTrack={currentAPKTrack}/>
              <VisibilityToggle value={apkData.isPublic} onChange={v=>setApkData(p=>({...p,isPublic:v}))}/>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                <FormField label="Version Name" required>
                  <input style={S.input} name="versionName" value={apkData.versionName} onChange={handleChange} placeholder="e.g. 2.0.0"/>
                </FormField>
                <FormField label="Version Code" required>
                  <input style={S.input} name="versionCode" type="number" min="1" value={apkData.versionCode} onChange={handleChange} placeholder="e.g. 200"/>
                </FormField>
              </div>

              <FormField label="SHA Key" required hint="SHA1 or SHA256 fingerprint — checksum auto-generated on server">
                <input style={{...S.input,fontFamily:'monospace',fontSize:13}} name="shaKey" value={apkData.shaKey} onChange={handleChange} placeholder="AB:CD:EF:12:34:56:78:90:AB:CD:EF:12"/>
              </FormField>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                <FormField label="Min SDK"><input style={S.input} name="minSdkVersion" value={apkData.minSdkVersion} onChange={handleChange} placeholder="21"/></FormField>
                <FormField label="Target SDK"><input style={S.input} name="targetSdkVersion" value={apkData.targetSdkVersion} onChange={handleChange} placeholder="33"/></FormField>
              </div>

              {showRollout && (
                <div>
                  <label style={S.label}>Rollout Percentage — <strong>{apkData.rolloutPercentage}%</strong></label>
                  <input type="range" min="1" max="100" step="1" value={apkData.rolloutPercentage}
                    onChange={e=>setApkData(p=>({...p,rolloutPercentage:+e.target.value}))} style={{width:'100%',marginTop:8}}/>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'#94a3b8',marginTop:4}}>
                    <span>1% staged</span><span>100% full release</span>
                  </div>
                </div>
              )}

              <FormField label="Release Notes / What's New">
                <textarea style={{...S.input,minHeight:90,resize:'vertical',fontFamily:'inherit',marginTop:2}}
                  name="releaseNotes" value={apkData.releaseNotes} onChange={handleChange}
                  placeholder={'• Bug fixes\n• New feature\n• Performance improvements'}/>
              </FormField>
            </div>
          )}

          {/* Step 3 — review */}
          {step===3 && (
            <div>
              <div style={{background:'#f8fafc',borderRadius:10,padding:20,marginBottom:16}}>
                <div style={{fontWeight:700,fontSize:15,marginBottom:14,color:'#1e293b'}}>Release Summary</div>
                <div style={{background:sel.bg,border:`1px solid ${sel.color}40`,borderRadius:8,padding:'10px 14px',marginBottom:14,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <TrackBadge track={apkData.track}/>
                  <VisibilityBadge isPublic={apkData.isPublic}/>
                  <span style={{fontSize:13,color:'#374151'}}>
                    {apkData.track==='production'?'Goes live to all users after publish.'
                    :apkData.track==='beta'?'Open beta — opted-in testers.'
                    :apkData.track==='alpha'?'Closed alpha — invite-only testers.'
                    :'Internal review — up to 100 testers.'}
                  </span>
                </div>
                {[
                  ['File',       apkFile?.name],
                  ['Size',       `${(apkFile?.size/1024/1024).toFixed(1)} MB`],
                  ['Version',    `${apkData.versionName} (Code: ${apkData.versionCode})`],
                  ['Track',      <TrackBadge key="t" track={apkData.track}/>],
                  ['Visibility', <VisibilityBadge key="v" isPublic={apkData.isPublic}/>],
                  ...(showRollout?[['Rollout',`${apkData.rolloutPercentage}%`]]:[]),
                  ['Min SDK',    apkData.minSdkVersion],
                  ['Target SDK', apkData.targetSdkVersion],
                  ['SHA Key',    <span key="sha" style={{fontFamily:'monospace',fontSize:12,wordBreak:'break-all'}}>{apkData.shaKey}</span>],
                  ['Checksum',   <span key="cs"  style={{fontSize:12,color:'#94a3b8'}}>Auto-generated on upload (SHA256)</span>],
                ].map(([k,v])=>(
                  <div key={k} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #e2e8f0',fontSize:13}}>
                    <span style={{color:'#64748b',flexShrink:0,marginRight:16}}>{k}</span>
                    <span style={{fontWeight:500,color:'#1e293b',textAlign:'right',maxWidth:380,overflow:'hidden',textOverflow:'ellipsis'}}>{v}</span>
                  </div>
                ))}
                {apkData.releaseNotes && (
                  <div style={{marginTop:14}}>
                    <div style={{fontSize:12,color:'#64748b',marginBottom:6,fontWeight:600}}>Release Notes</div>
                    <pre style={{fontSize:13,color:'#1e293b',whiteSpace:'pre-wrap',margin:0,fontFamily:'inherit',lineHeight:1.6}}>{apkData.releaseNotes}</pre>
                  </div>
                )}
              </div>

              {(uploading||progress>0) && (
                <div style={{marginBottom:16}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:6}}>
                    <span style={{color:'#3b82f6',fontWeight:600}}>{uploading?'Uploading...':'Upload complete'}</span>
                    <span style={{color:'#64748b'}}>{progress}%</span>
                  </div>
                  <ProgressBar pct={progress} color={done?'#10b981':'#3b82f6'}/>
                </div>
              )}
              {done && (
                <div style={{display:'flex',alignItems:'center',gap:10,background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:8,padding:'12px 16px'}}>
                  <span style={{fontSize:20}}>✅</span>
                  <span style={{color:'#166534',fontWeight:600}}>Upload successful! Closing...</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={S.ftr}>
          {step>1 && !uploading && !done && (
            <button style={S.btnSecondary} onClick={()=>{setErrors([]);setStep(s=>s-1);}}>← Back</button>
          )}
          <div style={{flex:1}}/>
          {!uploading && !done && <button style={S.btnSecondary} onClick={onClose}>Cancel</button>}
          {step===2 && <button style={S.btnPrimary} onClick={goReview}>Review Release →</button>}
          {step===3 && !uploading && !done && (
            <button style={{...S.btnPrimary,background:sel.color,borderColor:sel.color}} onClick={handlePublish}>
              <Upload size={15}/> Publish to {sel.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Add / Edit App Modal
───────────────────────────────────────────── */
const AppFormModal = ({ mode, formData, setFormData, onSubmit, onClose, formErrors, formSubmitting, previewLogo, previewSplash, previewIcon, handleFileChange, handleFeatureToggle, templates, availableFeatures }) => {
  const handleInputChange = e => setFormData(p=>({...p,[e.target.name]:e.target.value}));
  return (
    <div style={S.modal} onClick={()=>!formSubmitting&&onClose()}>
      <div style={S.card} onClick={e=>e.stopPropagation()}>
        <div style={S.hdr}>
          <h2 style={S.title}>{mode==='add'?'Create New App':'Edit App'}</h2>
          <button style={S.iconBtn} onClick={onClose} disabled={formSubmitting}><X size={20}/></button>
        </div>
        <form onSubmit={onSubmit} style={{display:'flex',flexDirection:'column',overflow:'hidden',flex:1}}>
          <div style={{padding:24,overflowY:'auto',flex:1}}>
            <ErrorBox errors={formErrors}/>
            <SectionTitle>Basic Information</SectionTitle>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:28}}>
              <FormField label="App Name" required><input style={S.input} name="appName" value={formData.appName} onChange={handleInputChange} required placeholder="My Awesome App"/></FormField>
              <FormField label="Package Name" required><input style={S.input} name="packageName" value={formData.packageName} onChange={handleInputChange} required placeholder="com.company.appname"/></FormField>
              <FormField label="Version"><input style={S.input} name="version" value={formData.version} onChange={handleInputChange} placeholder="1.0.0"/></FormField>
              <FormField label="Target SDK"><input style={S.input} name="targetSdkVersion" value={formData.targetSdkVersion} onChange={handleInputChange} placeholder="33"/></FormField>
              <div style={{gridColumn:'1 / -1'}}>
                <FormField label="Description"><textarea style={{...S.input,minHeight:80,resize:'vertical',fontFamily:'inherit'}} name="description" value={formData.description} onChange={handleInputChange} placeholder="Describe your app..."/></FormField>
              </div>
            </div>
            <SectionTitle>Choose Template</SectionTitle>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10,marginBottom:28}}>
              {templates.map(t=>(
                <div key={t.id} onClick={()=>setFormData(p=>({...p,template:t.id}))}
                  style={{padding:'14px 10px',borderRadius:10,textAlign:'center',cursor:'pointer',transition:'all .15s',border:`2px solid ${formData.template===t.id?t.color:'#e2e8f0'}`,background:formData.template===t.id?`${t.color}18`:'#fff'}}>
                  <div style={{fontSize:28,marginBottom:6}}>{t.icon}</div>
                  <div style={{fontSize:12,fontWeight:500,color:'#1e293b'}}>{t.name}</div>
                </div>
              ))}
            </div>
            <SectionTitle><Palette size={16} style={{marginRight:6}}/>Color Scheme</SectionTitle>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:28}}>
              {[['primaryColor','Primary Color'],['secondaryColor','Secondary Color'],['accentColor','Accent Color']].map(([key,label])=>(
                <FormField key={key} label={label}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <input type="color" name={key} value={formData[key]} onChange={handleInputChange} style={{width:44,height:38,padding:3,border:'1px solid #e2e8f0',borderRadius:6,cursor:'pointer'}}/>
                    <input type="text" style={{...S.input,width:100}} value={formData[key]} onChange={e=>setFormData(p=>({...p,[key]:e.target.value}))}/>
                  </div>
                </FormField>
              ))}
            </div>
            <SectionTitle><ImageIcon size={16} style={{marginRight:6}}/>App Assets</SectionTitle>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16,marginBottom:28}}>
              {[['logo','App Logo',previewLogo],['splash','Splash Screen',previewSplash],['icon','App Icon',previewIcon]].map(([type,label,preview])=>(
                <FormField key={type} label={label}>
                  <input type="file" accept="image/*" onChange={e=>handleFileChange(e,type)}/>
                  {preview&&<img src={preview} alt={label} style={{width:80,height:80,objectFit:'contain',border:'1px solid #e2e8f0',borderRadius:8,marginTop:8}}/>}
                </FormField>
              ))}
            </div>
            <SectionTitle><Code size={16} style={{marginRight:6}}/>App Features</SectionTitle>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:10}}>
              {availableFeatures.map(f=>{
                const on=formData.features.includes(f);
                return (
                  <div key={f} onClick={()=>handleFeatureToggle(f)}
                    style={{padding:'10px 12px',borderRadius:8,cursor:'pointer',transition:'all .15s',border:`2px solid ${on?'#3b82f6':'#e2e8f0'}`,background:on?'#eff6ff':'#fff',display:'flex',alignItems:'center',gap:8,fontSize:13,fontWeight:on?600:400,color:on?'#1d4ed8':'#374151'}}>
                    <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${on?'#3b82f6':'#d1d5db'}`,background:on?'#3b82f6':'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      {on&&<span style={{color:'#fff',fontSize:10,fontWeight:700}}>✓</span>}
                    </div>{f}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={S.ftr}>
            <button type="button" style={S.btnSecondary} onClick={onClose} disabled={formSubmitting}>Cancel</button>
            <button type="submit" style={S.btnPrimary} disabled={formSubmitting}>{formSubmitting?'Saving...':(mode==='add'?'Create App':'Update App')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   View App Modal
───────────────────────────────────────────── */
const ViewModal = ({ app, templates, onClose, onEdit, onBuild, onUploadAPK, onViewHistory, onDownloadLatest }) => {
  const template = templates.find(t=>t.id===app.template);
  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{...S.card,maxWidth:700}} onClick={e=>e.stopPropagation()}>
        <div style={S.hdr}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:42,height:42,borderRadius:10,background:(app.primaryColor||'#3b82f6')+'25',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20}}>{template?.icon||'📱'}</div>
            <div>
              <h2 style={{...S.title,marginBottom:2}}>{app.appName}</h2>
              <div style={{fontSize:12,color:'#64748b',fontFamily:'monospace'}}>{app.packageName}</div>
            </div>
          </div>
          <button style={S.iconBtn} onClick={onClose}><X size={20}/></button>
        </div>
        <div style={{padding:'20px 24px',overflowY:'auto',flex:1}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
            {[['Status',<StatusBadge key="s" status={app.status}/>],['Version',`v${app.version}`],['Builds',app.builds??'—'],['Downloads',app.downloads?.toLocaleString()??'0'],['Created',app.createdAt?new Date(app.createdAt).toLocaleDateString('en-IN'):'—'],['Last Build',app.lastBuildDate?new Date(app.lastBuildDate).toLocaleDateString('en-IN'):'Never']].map(([k,v])=>(
              <div key={k} style={{background:'#f8fafc',borderRadius:8,padding:'12px 14px'}}>
                <div style={{fontSize:11,color:'#64748b',marginBottom:4}}>{k}</div>
                <div style={{fontSize:14,fontWeight:600,color:'#1e293b'}}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{background:'#f8fafc',borderRadius:8,padding:'14px 16px',marginBottom:20}}>
            <div style={{fontSize:13,fontWeight:600,marginBottom:10,color:'#1e293b'}}>Color Scheme</div>
            <div style={{display:'flex',gap:20}}>
              {[['Primary',app.primaryColor],['Secondary',app.secondaryColor||'#1976D2'],['Accent',app.accentColor||'#FF5722']].map(([l,c])=>(
                <div key={l}>
                  <div style={{fontSize:11,color:'#64748b',marginBottom:4}}>{l}</div>
                  <div style={{width:52,height:36,background:c,borderRadius:6,border:'1px solid #e2e8f0'}}/>
                  <div style={{fontSize:10,color:'#94a3b8',marginTop:2,fontFamily:'monospace'}}>{c}</div>
                </div>
              ))}
            </div>
          </div>
          {app.features?.length>0&&(
            <div style={{marginBottom:20}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:8,color:'#1e293b'}}>Enabled Features</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                {app.features.map(f=><Badge key={f} color="#1d4ed8" bg="#dbeafe">{f}</Badge>)}
              </div>
            </div>
          )}
          <div style={{border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden'}}>
            <div style={{background:'#f8fafc',padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:'1px solid #e2e8f0'}}>
              <div style={{fontWeight:700,fontSize:14,color:'#1e293b',display:'flex',alignItems:'center',gap:8}}><Package size={16} color="#3b82f6"/> APK / AAB Management</div>
              <div style={{display:'flex',gap:8}}>
                <button style={S.btnSecondary} onClick={()=>{onClose();onViewHistory(app);}}><History size={14}/> Version History</button>
                <button style={S.btnPrimary}   onClick={()=>{onClose();onUploadAPK(app);}}><Upload size={14}/> Upload New APK</button>
              </div>
            </div>
            <div style={{padding:16}}>
              {app.latestAPK?(
                <div style={{display:'flex',alignItems:'center',gap:14}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#1e293b',marginBottom:4,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                      Latest: v{app.latestAPK.versionName}
                      <TrackBadge track={app.latestAPK.track}/>
                      <VisibilityBadge isPublic={app.latestAPK.isPublic!==false}/>
                      {app.latestAPK.isPublished&&<PublishBadge published={true}/>}
                      <Badge color="#166634" bg="#dcfce7">Active</Badge>
                    </div>
                    <div style={{fontSize:12,color:'#64748b',marginBottom:6}}>{app.latestAPK.fileName} · {app.latestAPK.fileSize} · Uploaded {app.latestAPK.uploadedAt?new Date(app.latestAPK.uploadedAt).toLocaleDateString('en-IN'):'—'}</div>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <span style={{fontSize:12,color:'#64748b'}}>Rollout: {app.latestAPK.rolloutPercentage}%</span>
                      <ProgressBar pct={app.latestAPK.rolloutPercentage} color="#10b981"/>
                    </div>
                  </div>
                  <button style={{...S.btnPrimary,background:'#10b981',borderColor:'#10b981'}} onClick={()=>onDownloadLatest(app.id||app._id)}><Download size={14}/> Download APK</button>
                </div>
              ):(
                <div style={{textAlign:'center',padding:'20px 0',color:'#94a3b8',fontSize:13}}>
                  No APK uploaded yet.
                  <button style={{...S.btnPrimary,marginLeft:12,display:'inline-flex'}} onClick={()=>{onClose();onUploadAPK(app);}}><Upload size={14}/> Upload First APK</button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={S.ftr}>
          <button style={S.btnSecondary} onClick={()=>{onClose();onEdit(app);}}><Edit2 size={15}/> Edit App</button>
          <button style={S.btnSecondary} onClick={()=>{onClose();onBuild(app);}}><Settings size={15}/> Build Now</button>
          <button style={S.btnPrimary}   onClick={()=>{onClose();onUploadAPK(app);}}><Upload size={15}/> Upload APK</button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Build Modal
───────────────────────────────────────────── */
const BuildModal = ({ app, templates, onClose, onConfirm }) => (
  <div style={S.modal} onClick={onClose}>
    <div style={{...S.card,maxWidth:480}} onClick={e=>e.stopPropagation()}>
      <div style={S.hdr}><h2 style={S.title}>Start New Build</h2><button style={S.iconBtn} onClick={onClose}><X size={20}/></button></div>
      <div style={{padding:'28px 24px',textAlign:'center'}}>
        <div style={{width:72,height:72,borderRadius:'50%',background:'#eff6ff',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}><RefreshCw size={36} color="#3b82f6"/></div>
        <div style={{fontSize:17,fontWeight:700,color:'#1e293b',marginBottom:8}}>Build "{app.appName}"?</div>
        <div style={{fontSize:13,color:'#64748b',marginBottom:20}}>This will trigger a new build with the current configuration.</div>
        <div style={{background:'#f8fafc',borderRadius:8,padding:'14px 16px',textAlign:'left',marginBottom:16,fontSize:13}}>
          {[['Version',`v${app.version}`],['Template',templates.find(t=>t.id===app.template)?.name],['Target SDK',app.targetSdkVersion]].map(([k,v])=>(
            <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid #e2e8f0'}}><span style={{color:'#64748b'}}>{k}</span><strong>{v}</strong></div>
          ))}
        </div>
        <div style={{fontSize:12,color:'#f59e0b'}}>⚠️ This process may take several minutes.</div>
      </div>
      <div style={S.ftr}>
        <button style={S.btnSecondary} onClick={onClose}>Cancel</button>
        <button style={S.btnPrimary}   onClick={onConfirm}><Settings size={15}/> Start Build</button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
const AppCustomizationPage = () => {
  const [apps,setApps]                     = useState([]);
  const [filteredApps,setFilteredApps]     = useState([]);
  const [loading,setLoading]               = useState(false);
  const [searchQuery,setSearchQuery]       = useState('');
  const [filterStatus,setFilterStatus]     = useState('All');
  const [filterTemplate,setFilterTemplate] = useState('All');
  const [sortField,setSortField]           = useState('createdAt');
  const [sortDir,setSortDir]               = useState('desc');
  const [currentPage,setCurrentPage]       = useState(1);
  const [selectedApps,setSelectedApps]     = useState([]);
  const PER_PAGE = 10;

  const [showAdd,setShowAdd]               = useState(false);
  const [showEdit,setShowEdit]             = useState(false);
  const [showView,setShowView]             = useState(false);
  const [showBuild,setShowBuild]           = useState(false);
  const [showUploadAPK,setShowUploadAPK]   = useState(false);
  const [showAPKHistory,setShowAPKHistory] = useState(false);
  const [selectedApp,setSelectedApp]       = useState(null);
  const [formErrors,setFormErrors]         = useState([]);
  const [formBusy,setFormBusy]             = useState(false);

  const [formData,setFormData] = useState({ appName:'',packageName:'',template:'template_a',primaryColor:'#0D47A1',secondaryColor:'#1976D2',accentColor:'#FF5722',description:'',version:'1.0.0',minSdkVersion:'21',targetSdkVersion:'33',features:[] });
  const [logoFile,setLogoFile]     = useState(null);
  const [splashFile,setSplashFile] = useState(null);
  const [iconFile,setIconFile]     = useState(null);
  const [prevLogo,setPrevLogo]     = useState(null);
  const [prevSplash,setPrevSplash] = useState(null);
  const [prevIcon,setPrevIcon]     = useState(null);

  const templates = [
    {id:'template_a',name:'HRMS Template',icon:'👥',color:'#3b82f6'},
    {id:'template_b',name:'CRM Template',icon:'📊',color:'#8b5cf6'},
    {id:'template_c',name:'Delivery Template',icon:'🚚',color:'#10b981'},
    {id:'template_d',name:'E-Commerce',icon:'🛒',color:'#f59e0b'},
    {id:'template_e',name:'Healthcare',icon:'🏥',color:'#ec4899'},
    {id:'template_f',name:'Education',icon:'📚',color:'#6366f1'},
  ];
  const availableFeatures = ['Push Notifications','Offline Mode','Dark Mode','Multi-language','Analytics','In-App Purchase','Social Login','Chat Support','Maps Integration','Camera Access','Biometric Auth','Cloud Sync'];

  useEffect(()=>{fetchApps();},[]);
  useEffect(()=>{
    let r=[...apps];
    if(searchQuery) r=r.filter(a=>(a.appName||'').toLowerCase().includes(searchQuery.toLowerCase())||(a.packageName||'').toLowerCase().includes(searchQuery.toLowerCase()));
    if(filterStatus!=='All') r=r.filter(a=>a.status===filterStatus);
    if(filterTemplate!=='All') r=r.filter(a=>a.template===filterTemplate);
    r.sort((a,b)=>{let av=a[sortField],bv=b[sortField];if(typeof av==='string') return sortDir==='asc'?av.localeCompare(bv):bv.localeCompare(av);return sortDir==='asc'?av-bv:bv-av;});
    setFilteredApps(r); setCurrentPage(1);
  },[apps,searchQuery,filterStatus,filterTemplate,sortField,sortDir]);

  const fetchApps = async()=>{setLoading(true);const res=await customAppController.getAll();if(res.success) setApps(res.data);else alert('Error: '+res.message);setLoading(false);};
  const handleFileChange=(e,type)=>{const file=e.target.files[0];if(!file)return;const r=new FileReader();r.onload=()=>{if(type==='logo'){setPrevLogo(r.result);setLogoFile(file);}else if(type==='splash'){setPrevSplash(r.result);setSplashFile(file);}else{setPrevIcon(r.result);setIconFile(file);}};r.readAsDataURL(file);};
  const handleFeatureToggle=f=>setFormData(p=>({...p,features:p.features.includes(f)?p.features.filter(x=>x!==f):[...p.features,f]}));
  const resetForm=()=>{setFormData({appName:'',packageName:'',template:'template_a',primaryColor:'#0D47A1',secondaryColor:'#1976D2',accentColor:'#FF5722',description:'',version:'1.0.0',minSdkVersion:'21',targetSdkVersion:'33',features:[]});setLogoFile(null);setSplashFile(null);setIconFile(null);setPrevLogo(null);setPrevSplash(null);setPrevIcon(null);setFormErrors([]);};

  const handleAdd=()=>{resetForm();setShowAdd(true);};
  const handleAddSubmit=async e=>{e.preventDefault();setFormBusy(true);setFormErrors([]);const res=await customAppController.create(formData,logoFile,splashFile,iconFile);if(res.success){setShowAdd(false);fetchApps();alert('✅ '+res.message);}else setFormErrors(res.errors||[res.message]);setFormBusy(false);};
  const handleEdit=app=>{setSelectedApp(app);setFormData({appName:app.appName||'',packageName:app.packageName||'',template:app.template||'template_a',primaryColor:app.primaryColor||'#0D47A1',secondaryColor:app.secondaryColor||'#1976D2',accentColor:app.accentColor||'#FF5722',description:app.description||'',version:app.version||'1.0.0',minSdkVersion:app.minSdkVersion||'21',targetSdkVersion:app.targetSdkVersion||'33',features:app.features||[]});setLogoFile(null);setSplashFile(null);setIconFile(null);setPrevLogo(app.logoUrl||null);setPrevSplash(app.splashUrl||null);setPrevIcon(app.iconUrl||null);setFormErrors([]);setShowEdit(true);};
  const handleEditSubmit=async e=>{e.preventDefault();setFormBusy(true);setFormErrors([]);const res=await customAppController.update(selectedApp.id||selectedApp._id,formData,logoFile,splashFile,iconFile);if(res.success){setShowEdit(false);fetchApps();alert('✅ '+res.message);}else setFormErrors(res.errors||[res.message]);setFormBusy(false);};
  const handleDelete=async app=>{if(!window.confirm(`Delete "${app.appName}"?`))return;const res=await customAppController.delete(app.id||app._id);if(res.success){fetchApps();alert('✅ '+res.message);}else alert('❌ '+res.message);};
  const handleBulkDelete=async()=>{if(!selectedApps.length){alert('Select apps first.');return;}if(!window.confirm(`Delete ${selectedApps.length} apps?`))return;const res=await customAppController.bulkDelete(selectedApps);if(res.success){setSelectedApps([]);fetchApps();alert('✅ '+res.message);}else alert('❌ '+res.message);};
  const handleBuildConfirm=async()=>{if(!selectedApp)return;setShowBuild(false);const res=await customAppController.buildApp(selectedApp.id||selectedApp._id);if(res.success){fetchApps();alert('✅ '+res.message);}else alert('❌ '+res.message);};
  const handleDownloadLatest=async appId=>{const res=await customAppController.downloadLatestAPK(appId);if(res.success&&res.downloadUrl){const a=document.createElement('a');a.href=res.downloadUrl;a.download=res.fileName||'app.apk';a.click();}else alert('❌ '+(res.message||'Download failed'));};
  const handleDownloadSpecific=async(appId,apkId)=>{const res=await customAppController.downloadAPK(appId,apkId);if(res.success&&res.downloadUrl){const a=document.createElement('a');a.href=res.downloadUrl;a.download=res.fileName||'app.apk';a.click();}else alert('❌ '+(res.message||'Download failed'));};
  const handleExport=async()=>{const filters={};if(filterStatus!=='All')filters.status=filterStatus;if(filterTemplate!=='All')filters.template=filterTemplate;const res=await customAppController.export(filters,'csv');if(res.success){const url=URL.createObjectURL(new Blob([res.data],{type:'text/csv'}));const a=document.createElement('a');a.href=url;a.download=`apps-${new Date().toISOString().split('T')[0]}.csv`;a.click();URL.revokeObjectURL(url);}else alert('❌ '+res.message);};
  const handleSort=f=>{if(sortField===f)setSortDir(d=>d==='asc'?'desc':'asc');else{setSortField(f);setSortDir('asc');}};

  const last=currentPage*PER_PAGE; const first=last-PER_PAGE;
  const pageApps=filteredApps.slice(first,last);
  const totalPages=Math.ceil(filteredApps.length/PER_PAGE);
  const stats={total:apps.length,published:apps.filter(a=>a.status==='Published').length,building:apps.filter(a=>a.status==='Building').length,draft:apps.filter(a=>a.status==='Draft').length,downloads:apps.reduce((s,a)=>s+(a.downloads||0),0)};

  return (
    <div style={{padding:0}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24,flexWrap:'wrap',gap:12}}>
        <h1 style={{fontSize:26,fontWeight:800,color:'#1e293b',margin:0}}>Custom App Builder</h1>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          {selectedApps.length>0&&<button style={S.btnDanger} onClick={handleBulkDelete}><Trash2 size={16}/> Delete ({selectedApps.length})</button>}
          <button style={S.btnSecondary} onClick={handleExport}><Download size={16}/> Export</button>
          <button style={S.btnPrimary}   onClick={handleAdd}><Plus size={16}/> Create New App</button>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:14,marginBottom:24}}>
        {[['Total Apps',stats.total,'#3b82f6',<Smartphone size={22} color="#3b82f6"/>],['Published',stats.published,'#10b981',<CheckCircle2 size={22} color="#10b981"/>],['Building',stats.building,'#f59e0b',<Clock size={22} color="#f59e0b"/>],['Drafts',stats.draft,'#64748b',<Package size={22} color="#64748b"/>],['Downloads',stats.downloads.toLocaleString(),'#8b5cf6',<Download size={22} color="#8b5cf6"/>]].map(([label,val,color,icon])=>(
          <div key={label} style={{background:'#fff',padding:'18px 20px',borderRadius:12,boxShadow:'0 1px 4px rgba(0,0,0,.07)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}><span style={{fontSize:13,color:'#64748b'}}>{label}</span>{icon}</div>
            <div style={{fontSize:28,fontWeight:800,color}}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{display:'flex',gap:10,marginBottom:18,flexWrap:'wrap',alignItems:'center'}}>
        <div style={{flex:1,minWidth:220,position:'relative'}}>
          <Search size={16} color="#94a3b8" style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)'}}/>
          <input type="text" placeholder="Search apps..." value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} style={{...S.input,paddingLeft:38}}/>
        </div>
        <select value={filterStatus}   onChange={e=>setFilterStatus(e.target.value)}   style={{...S.input,width:'auto',cursor:'pointer'}}>
          {['All','Published','Building','Draft','Failed'].map(o=><option key={o} value={o}>{o==='All'?'All Status':o}</option>)}
        </select>
        <select value={filterTemplate} onChange={e=>setFilterTemplate(e.target.value)} style={{...S.input,width:'auto',cursor:'pointer'}}>
          <option value="All">All Templates</option>
          {templates.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div style={{background:'#fff',borderRadius:12,boxShadow:'0 1px 4px rgba(0,0,0,.07)',overflow:'hidden'}}>
        {loading?<div style={S.center}>Loading apps...</div>:pageApps.length===0?<div style={S.center}>No apps found.</div>:(
          <>
            <div style={{overflowX:'auto'}}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={{...S.th,width:46}}>
                      <input type="checkbox" style={{width:16,height:16,cursor:'pointer'}} checked={selectedApps.length===pageApps.length&&pageApps.length>0} onChange={()=>setSelectedApps(selectedApps.length===pageApps.length?[]:pageApps.map(a=>a.id))}/>
                    </th>
                    {[['appName','App Name'],['packageName','Package'],['template','Template'],['status','Status'],['version','Version'],['builds','Builds'],['downloads','Downloads']].map(([f,l])=>(
                      <th key={f} style={{...S.th,cursor:'pointer',userSelect:'none'}} onClick={()=>handleSort(f)}>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>{l}{sortField===f&&(sortDir==='asc'?<ChevronUp size={14}/>:<ChevronDown size={14}/>)}</div>
                      </th>
                    ))}
                    <th style={S.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageApps.map(app=>{
                    const tmpl=templates.find(t=>t.id===app.template);
                    return (
                      <tr key={app.id} onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={S.td}><input type="checkbox" style={{width:16,height:16,cursor:'pointer'}} checked={selectedApps.includes(app.id)} onChange={()=>setSelectedApps(p=>p.includes(app.id)?p.filter(x=>x!==app.id):[...p,app.id])} onClick={e=>e.stopPropagation()}/></td>
                        <td style={{...S.td,cursor:'pointer'}} onClick={()=>{setSelectedApp(app);setShowView(true);}}>
                          <div style={{display:'flex',alignItems:'center',gap:10}}>
                            <div style={{width:38,height:38,borderRadius:8,background:(app.primaryColor||'#3b82f6')+'22',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>{tmpl?.icon||'📱'}</div>
                            <div>
                              <div style={{fontWeight:600,fontSize:13,color:'#1e293b'}}>{app.appName}</div>
                              <div style={{fontSize:11,color:'#94a3b8'}}>
                                {app.latestAPK
                                  ?<span style={{color:'#10b981',fontWeight:600,display:'inline-flex',alignItems:'center',gap:4}}>v{app.latestAPK.versionName} <TrackBadge track={app.latestAPK.track}/></span>
                                  :'No APK uploaded'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={S.td}><span style={{fontSize:12,fontFamily:'monospace',color:'#64748b'}}>{app.packageName}</span></td>
                        <td style={S.td}><Badge color={tmpl?.color||'#6b7280'} bg={(tmpl?.color||'#6b7280')+'20'}>{tmpl?.name||'Unknown'}</Badge></td>
                        <td style={S.td}><StatusBadge status={app.status}/></td>
                        <td style={S.td}><span style={{fontWeight:600,fontSize:13}}>v{app.version}</span></td>
                        <td style={S.td}><span style={{fontSize:13,color:'#475569'}}>{app.builds??'—'}</span></td>
                        <td style={S.td}><span style={{fontWeight:700,fontSize:13,color:'#3b82f6'}}>{app.downloads?.toLocaleString()??'0'}</span></td>
                        <td style={S.td}>
                          <div style={{display:'flex',gap:2}}>
                            <button style={S.iconBtn} title="View"        onClick={e=>{e.stopPropagation();setSelectedApp(app);setShowView(true);}}><Eye     size={17} color="#64748b"/></button>
                            <button style={S.iconBtn} title="Edit"        onClick={e=>{e.stopPropagation();handleEdit(app);}}><Edit2   size={17} color="#3b82f6"/></button>
                            <button style={S.iconBtn} title="Upload APK"  onClick={e=>{e.stopPropagation();setSelectedApp(app);setShowUploadAPK(true);}}><Upload  size={17} color="#10b981"/></button>
                            <button style={S.iconBtn} title="APK History" onClick={e=>{e.stopPropagation();setSelectedApp(app);setShowAPKHistory(true);}}><History size={17} color="#8b5cf6"/></button>
                            <button style={S.iconBtn} title="Download"    onClick={e=>{e.stopPropagation();handleDownloadLatest(app.id||app._id);}}><Download size={17} color="#0ea5e9"/></button>
                            <button style={S.iconBtn} title="Build"       onClick={e=>{e.stopPropagation();setSelectedApp(app);setShowBuild(true);}}><Settings size={17} color="#f59e0b"/></button>
                            <button style={S.iconBtn} title="Delete"      onClick={e=>{e.stopPropagation();handleDelete(app);}}><Trash2  size={17} color="#ef4444"/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',borderTop:'1px solid #f1f5f9'}}>
              <span style={{fontSize:13,color:'#64748b'}}>Showing {first+1}–{Math.min(last,filteredApps.length)} of {filteredApps.length} apps</span>
              <div style={{display:'flex',gap:6}}>
                <button style={S.btnSecondary} onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={currentPage===1}>Previous</button>
                <span style={{padding:'9px 14px',fontSize:13,color:'#64748b'}}>Page {currentPage} of {totalPages}</span>
                <button style={S.btnSecondary} onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} disabled={currentPage===totalPages}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {(showAdd||showEdit)&&<AppFormModal mode={showAdd?'add':'edit'} formData={formData} setFormData={setFormData} onSubmit={showAdd?handleAddSubmit:handleEditSubmit} onClose={()=>{setShowAdd(false);setShowEdit(false);}} formErrors={formErrors} formSubmitting={formBusy} previewLogo={prevLogo} previewSplash={prevSplash} previewIcon={prevIcon} handleFileChange={handleFileChange} handleFeatureToggle={handleFeatureToggle} templates={templates} availableFeatures={availableFeatures}/>}
      {showView&&selectedApp&&<ViewModal app={selectedApp} templates={templates} onClose={()=>setShowView(false)} onEdit={a=>{setShowView(false);handleEdit(a);}} onBuild={a=>{setShowView(false);setSelectedApp(a);setShowBuild(true);}} onUploadAPK={a=>{setShowView(false);setSelectedApp(a);setShowUploadAPK(true);}} onViewHistory={a=>{setShowView(false);setSelectedApp(a);setShowAPKHistory(true);}} onDownloadLatest={handleDownloadLatest}/>}
      {showBuild&&selectedApp&&<BuildModal app={selectedApp} templates={templates} onClose={()=>setShowBuild(false)} onConfirm={handleBuildConfirm}/>}
      {showUploadAPK&&selectedApp&&<APKUploadModal app={selectedApp} onClose={()=>setShowUploadAPK(false)} onSuccess={msg=>{fetchApps();alert('✅ '+msg);}}/>}
      {showAPKHistory&&selectedApp&&<APKHistoryModal app={selectedApp} onClose={()=>setShowAPKHistory(false)} onDownload={handleDownloadSpecific} onChangeTrack={msg=>{fetchApps();alert('✅ '+msg);}}/>}
    </div>
  );
};

export default AppCustomizationPage;